import {useEffect, useRef, useState} from "react";
import {Upload as AntUpload} from "antd";
import {useTranslation} from "react-i18next";
import cn from "classnames";
// local
import {showSuccess, showWarning} from "helpers/notifications.helper";
import {beforeUpload} from "helpers/uploader";
import {fileExtension, fileExtensionFromType, fileType} from "constants/file.consts";

const noFiles = [];

export const useUpload = ({
  files = noFiles,
  onChange,
  uploadRequest,
  removeRequest,
  disabledUpload,
  customFileTypes,
  customFileSize,
  multiple = false,
  maxFilesCount = Number.MAX_SAFE_INTEGER,
}) => {
  const {t} = useTranslation();
  const [uploadingCount, setUploadingCount] = useState(0);
  const loading = uploadingCount > 0;

  const filesRef = useRef(files);
  filesRef.current = files;

  const pendingCountRef = useRef(0);
  const warnedSelectionRef = useRef(null);
  const isUploadDisabled =
    disabledUpload || files.length + uploadingCount >= maxFilesCount;

  const changeFiles = (getNextFiles) => {
    filesRef.current = getNextFiles(filesRef.current);
    onChange && onChange(filesRef.current);
  };

  const validateFile = (file, selectedFiles) => {
    const isValid = beforeUpload(file, customFileTypes, customFileSize);
    if (isValid !== true) return isValid;
    if (filesRef.current.length + pendingCountRef.current >= maxFilesCount) {
      if (warnedSelectionRef.current !== selectedFiles) {
        warnedSelectionRef.current = selectedFiles;
        showWarning(t("FILE_UPLOAD_TOO_MANY_FILES").format(maxFilesCount));
      }
      return AntUpload.LIST_IGNORE;
    }
    pendingCountRef.current += 1;
    return true;
  };

  const customRequest = async ({file, onError}) => {
    setUploadingCount((count) => count + 1);
    try {
      const uploadedFile = uploadRequest ? await uploadRequest(file) : file;
      uploadRequest && showSuccess(t("FILE_UPLOADED_SUCCESSFULLY").format(file.name));
      changeFiles((currentFiles) => [uploadedFile, ...currentFiles]);
    } catch (error) {
      onError(error);
    } finally {
      pendingCountRef.current -= 1;
      setUploadingCount((count) => count - 1);
    }
  };

  const removeFile = async (file) => {
    if (removeRequest) await removeRequest(file.id);
    changeFiles((currentFiles) => currentFiles.filter((item) => item !== file));
  };

  const addFiles = (droppedFiles) => {
    if (disabledUpload) return;
    const selectedFiles = multiple ? [...droppedFiles] : [...droppedFiles].slice(0, 1);
    selectedFiles.forEach((file) => {
      file.uid = window.crypto.randomUUID();
    });
    selectedFiles
      .filter((file) => validateFile(file, selectedFiles) === true)
      .forEach((file) => customRequest({file, onError: () => {}}));
  };

  return {
    files,
    loading,
    isUploadDisabled,
    removeFile,
    addFiles,
    uploadProps: {
      name: "file",
      beforeUpload: validateFile,
      customRequest,
      showUploadList: false,
      disabled: isUploadDisabled,
      multiple,
    },
  };
};

const isFilesDrag = (event) =>
  Array.from(event.dataTransfer?.types ?? []).includes("Files");

const isForbiddenType = (mimeType, fileTypes) => {
  const extension = fileExtensionFromType[mimeType];
  if (extension) return !fileTypes.includes(extension);
  return !!mimeType && mimeType !== fileType[fileExtension.STREAM];
};

const isForbiddenDrag = (event, fileTypes) => {
  if (!fileTypes) return false;
  const items = Array.from(event.dataTransfer.items ?? []).filter(
    (item) => item.kind === "file"
  );
  return !!items.length && items.every((item) => isForbiddenType(item.type, fileTypes));
};

export const useFileDrop = ({onFiles, disabled, fileTypes}) => {
  const [dragState, setDragState] = useState(null);
  const enteredCountRef = useRef(0);
  const areaRef = useRef(null);

  // The counter misses the dragleave of an element that a re-render replaced under the
  // pointer, so the drag also ends when it enters any element outside the area
  useEffect(() => {
    if (!dragState) return;
    const onDocumentDragEnter = (event) => {
      if (areaRef.current?.contains(event.target)) return;
      enteredCountRef.current = 0;
      setDragState(null);
    };
    document.addEventListener("dragenter", onDocumentDragEnter, true);
    return () => document.removeEventListener("dragenter", onDocumentDragEnter, true);
  }, [dragState]);

  const refreshDragState = (event) => {
    if (disabled) return;
    setDragState(isForbiddenDrag(event, fileTypes) ? "forbidden" : "allowed");
  };

  const onDragEnter = (event) => {
    if (!isFilesDrag(event)) return;
    areaRef.current = event.currentTarget;
    enteredCountRef.current += 1;
    refreshDragState(event);
  };

  const onDragOver = (event) => {
    if (!isFilesDrag(event)) return;
    event.preventDefault();
    refreshDragState(event);
  };

  const onDragLeave = (event) => {
    if (!isFilesDrag(event)) return;
    enteredCountRef.current = Math.max(enteredCountRef.current - 1, 0);
    !enteredCountRef.current && setDragState(null);
  };

  const onDropCapture = (event) => {
    if (!isFilesDrag(event)) return;
    enteredCountRef.current = 0;
    setDragState(null);
    event.stopPropagation();
    event.preventDefault();
    !disabled && onFiles(Array.from(event.dataTransfer.files));
  };

  return {
    dropProps: {onDragEnter, onDragOver, onDragLeave, onDropCapture},
    dropClassName: cn("file-drop-area", {
      "__drag-over": dragState === "allowed",
      "__file-invalid": dragState === "forbidden",
    }),
  };
};
